import { type ChangeEvent, useRef } from 'react'

import { Button, Field, Layout, Text } from '@loadsmart/miranda-react'

import { Icon } from 'core/ui/components/icon/Icon'

import {
  CHARGE_ACCEPTED_FILE_EXTENSIONS,
  CHARGE_MAX_FILE_SIZE_IN_MB,
} from '../domain/ShipmentCharges'
import { formatFileSize } from '../presenters/shipmentChargePresenter'
import type { NewChargeState } from './hooks/useNewChargeForm'

type NewChargeAttachmentFieldProps = Pick<NewChargeState, 'attachmentFile' | 'errors'> & {
  isSubmitting: boolean
  onFileInputChange: (event: ChangeEvent<HTMLInputElement>) => void
  onRemoveAttachment: () => void
}

export function NewChargeAttachmentField({
  attachmentFile,
  errors,
  isSubmitting,
  onFileInputChange,
  onRemoveAttachment,
}: NewChargeAttachmentFieldProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)

  function onBrowseFile() {
    if (!fileInputRef.current) {
      return
    }
    fileInputRef.current.value = ''
    fileInputRef.current.click()
  }

  return (
    <Field
      status={errors.fileSize || errors.fileFormat ? 'danger' : 'default'}
      align={attachmentFile ? 'stretch' : 'flex-start'}
    >
      <Field.Label>Upload file (receipt or any proof)</Field.Label>
      {attachmentFile ? (
        <Layout.Group align="center" justify="space-between" gap="spacing-2">
          <Layout.Stack gap="none">
            <Text variant="body-sm">{attachmentFile.name}</Text>
            <Text variant="number-sm" color="color-text-secondary">
              {formatFileSize(attachmentFile.size)}
            </Text>
          </Layout.Stack>
          <Button
            type="button"
            variant="icon-secondary"
            size="small"
            aria-label="Remove attachment"
            onClick={onRemoveAttachment}
          >
            <Icon name="close" width={12} height={12} title="Remove attachment" />
          </Button>
        </Layout.Group>
      ) : (
        <>
          <Button
            id="attachment-button"
            type="button"
            variant="tertiary"
            size="small"
            leading={<Icon name="upload" width={12} height={12} title="" />}
            onClick={onBrowseFile}
            disabled={isSubmitting}
          >
            Browse
          </Button>
          <input
            ref={fileInputRef}
            aria-labelledby="attachment-button"
            data-testid="attachment-input"
            type="file"
            accept={CHARGE_ACCEPTED_FILE_EXTENSIONS.join(',')}
            onChange={onFileInputChange}
            style={{ display: 'none' }}
          />
        </>
      )}
      {errors.fileSize && <Field.Hint>{CHARGE_MAX_FILE_SIZE_IN_MB}mb max size</Field.Hint>}
      {errors.fileFormat && <Field.Hint>Pdf or image only</Field.Hint>}
    </Field>
  )
}
